import React, { useEffect, useState } from "react";
import JSZip from "jszip";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import { FileText, Printer, Download, Loader2 } from "lucide-react";

type Row = {
  id: string;
  week_start: string;
  week_end: string;
  jobs: number;
  total_payment: number;
  payslip_html: string | null;
  pdf_url: string | null;
};

const money = (n: number | null | undefined) => `$${Number(n || 0).toFixed(2)}`;
const ddmmyy = (ymd?: string | null) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-");
  if (!y || !m || !d) return ymd;
  return `${d}-${m}-${y.slice(-2)}`;
};
const thisMonth = () => new Date().toISOString().slice(0, 7); // YYYY-MM
const sanitizeForFile = (s: string) => s.replace(/[^a-zA-Z0-9_-]/g, "_");

const monthBounds = (ym: string) => {
  const [y, m] = ym.split("-").map(Number);
  const start = `${ym}-01`;
  const last = new Date(y, m, 0).getDate();
  const end = `${ym}-${String(last).padStart(2, "0")}`;
  return { start, end };
};

const printHtml = (html: string) => {
  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) return;
  w.document.open();
  w.document.write(html);
  w.document.close();
  setTimeout(() => {
    try { w.focus(); w.print(); } catch { /* ignore */ }
  }, 400);
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

interface Props {
  driverName: string;
}

const DriverPdfSummaries: React.FC<Props> = ({ driverName }) => {
  const [month, setMonth] = useState<string>(thisMonth());
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [zipping, setZipping] = useState(false);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { start, end } = monthBounds(month);
      const { data, error } = await supabase
        .from("driver_weekly_summaries")
        .select("id, week_start, week_end, jobs, total_payment, payslip_html, pdf_url")
        .eq("driver_name", driverName)
        .gte("week_start", start)
        .lte("week_start", end)
        .order("week_start", { ascending: true });
      if (error) throw error;
      setRows((data as any[]) || []);
      setSelected(new Set());
    } catch (e: any) {
      setError(e?.message || "Failed to load summaries");
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverName, month]);

  // Only rows with a real PDF file can be bundled into a zip -- an
  // old row with just payslip_html has nothing to fetch.
  const downloadableRows = rows.filter((r) => !!r.pdf_url);
  const allSelected = downloadableRows.length > 0 && downloadableRows.every((r) => selected.has(r.id));

  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(downloadableRows.map((r) => r.id)));
  };

  const downloadSelected = async () => {
    const toDownload = downloadableRows.filter((r) => selected.has(r.id));
    if (toDownload.length === 0) return;

    // A single PDF just downloads directly -- no need to zip one file.
    if (toDownload.length === 1) {
      window.open(toDownload[0].pdf_url!, "_blank");
      return;
    }

    setZipping(true);
    try {
      const zip = new JSZip();
      let failed = 0;
      await Promise.all(
        toDownload.map(async (r) => {
          try {
            const res = await fetch(r.pdf_url!);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const buf = await res.arrayBuffer();
            zip.file(`Payment-Summary-${r.week_start}-to-${r.week_end}.pdf`, buf);
          } catch {
            failed++;
          }
        }),
      );
      const blob = await zip.generateAsync({ type: "blob" });
      downloadBlob(blob, `Payment-Summaries-${sanitizeForFile(driverName)}-${month}.zip`);
      if (failed > 0) {
        toast({
          title: "Some files were skipped",
          description: `${failed} of ${toDownload.length} PDFs couldn't be downloaded and were left out of the zip.`,
          variant: "destructive",
        });
      }
    } catch (err: any) {
      toast({ title: "Download failed", description: err?.message ?? String(err), variant: "destructive" });
    } finally {
      setZipping(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end justify-between">
        <div>
          <div className="text-lg font-semibold">PDF Summaries</div>
          <div className="text-sm text-muted-foreground">
            Search a month to find your saved weekly payment summaries. Select the ones you want and download them together.
          </div>
        </div>
        <div className="flex flex-col">
          <label className="text-xs text-muted-foreground mb-1">Month</label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-[180px] bg-background" />
        </div>
      </div>

      {loading && <div className="text-sm text-muted-foreground p-4">Loading…</div>}
      {error && !loading && <div className="text-sm text-red-400 p-4">{error}</div>}
      {!loading && !error && rows.length === 0 && (
        <div className="text-sm text-muted-foreground p-6 border rounded-lg text-center">
          No summaries found for {month}.
        </div>
      )}

      {!loading && !error && downloadableRows.length > 0 && (
        <div className="flex items-center justify-between border rounded-lg px-3 py-2 bg-muted/10">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={allSelected} onCheckedChange={toggleAll} />
            Select all
          </label>
          <Button
            size="sm"
            className="bg-yellow-600 hover:bg-yellow-700 text-black"
            disabled={selected.size === 0 || zipping}
            onClick={downloadSelected}
          >
            {zipping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            {zipping ? "Preparing…" : `Download Selected${selected.size > 0 ? ` (${selected.size})` : ""}`}
          </Button>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((r) => (
          <div key={r.id} className="border rounded-lg p-4 flex flex-col gap-2 bg-muted/10">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <FileText className="w-4 h-4 text-yellow-500" />
                {ddmmyy(r.week_start)} → {ddmmyy(r.week_end)}
              </div>
              {r.pdf_url && <Checkbox checked={selected.has(r.id)} onCheckedChange={() => toggleRow(r.id)} />}
            </div>
            <div className="text-xs text-muted-foreground">{r.jobs} jobs</div>
            <div className="text-lg font-bold text-green-500">{money(r.total_payment)}</div>
            {r.pdf_url ? (
              <Button
                size="sm"
                variant="outline"
                className="mt-1 border-yellow-600 text-yellow-400 hover:bg-yellow-900"
                onClick={() => window.open(r.pdf_url!, "_blank")}
              >
                <FileText className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="mt-1 border-yellow-600 text-yellow-400 hover:bg-yellow-900"
                disabled={!r.payslip_html}
                onClick={() => r.payslip_html && printHtml(r.payslip_html)}
              >
                <Printer className="w-4 h-4 mr-2" />
                Open / Print PDF
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DriverPdfSummaries;
